import { supabase } from "@/integrations/supabase/client";

interface OrderItemForRouting {
  id: string;
  product_id: string | null;
  product_name: string;
  quantity: number;
  notes: string | null;
  combo_id: string | null;
  combo_selections: any;
}

function extractProductIdsFromComboSelections(comboSelections: any): string[] {
  const productIds: string[] = [];
  if (!comboSelections) return productIds;
  const slotSelections = comboSelections.slotSelections;
  if (slotSelections) {
    if (!Array.isArray(slotSelections)) {
      for (const products of Object.values(slotSelections)) {
        if (Array.isArray(products)) {
          for (const product of products as any[]) {
            if (product.productId) productIds.push(product.productId);
          }
        }
      }
    } else {
      for (const slot of slotSelections) {
        if ((slot as any).selectedProducts) {
          for (const product of (slot as any).selectedProducts) {
            if (product.productId) productIds.push(product.productId);
          }
        }
      }
    }
  }
  if (comboSelections.pizzaFlavors && Array.isArray(comboSelections.pizzaFlavors)) {
    for (const flavor of comboSelections.pizzaFlavors) {
      if (flavor.productId) productIds.push(flavor.productId);
    }
  }
  if (comboSelections.choices && Array.isArray(comboSelections.choices)) {
    for (const choice of comboSelections.choices) {
      if (choice.productId) productIds.push(choice.productId);
    }
  }
  return productIds;
}

/**
 * Pure function to create KDS ticket items for an order.
 * Can be called from any context (PDV, hooks, etc.)
 */
export async function createKdsItemsForOrder(
  restaurantId: string,
  orderId: string,
  orderItems: OrderItemForRouting[]
): Promise<void> {
  if (!restaurantId || orderItems.length === 0) return;

  const productIds = orderItems.map(i => i.product_id).filter(Boolean) as string[];
  const comboIds = orderItems.map(i => i.combo_id).filter(Boolean) as string[];

  // Extract product IDs from combo selections
  const comboProductIds: string[] = [];
  for (const item of orderItems) {
    if (item.combo_id && item.combo_selections) {
      comboProductIds.push(...extractProductIdsFromComboSelections(item.combo_selections));
    }
  }
  const allProductIds = [...new Set([...productIds, ...comboProductIds])];

  // Fetch product and combo production locations
  let products: any[] = [];
  if (allProductIds.length > 0) {
    const { data } = await supabase
      .from("products")
      .select("id, production_location_id")
      .in("id", allProductIds)
      .eq("restaurant_id", restaurantId);
    if (data) products = data;
  }

  let combos: any[] = [];
  if (comboIds.length > 0) {
    const { data } = await supabase
      .from("combos")
      .select("id, production_location_id")
      .in("id", comboIds)
      .eq("restaurant_id", restaurantId);
    if (data) combos = data;
  }

  // Resolve production_location_id for each item
  const inserts: any[] = [];
  const now = new Date().toISOString();

  for (const item of orderItems) {
    let locationId: string | null = null;

    if (item.combo_id) {
      const combo = combos.find((c: any) => c.id === item.combo_id);
      if (combo?.production_location_id) {
        locationId = combo.production_location_id;
      } else {
        const selectedProductIds = extractProductIdsFromComboSelections(item.combo_selections);
        if (selectedProductIds.length > 0) {
          const firstProduct = products.find((p: any) => selectedProductIds.includes(p.id));
          if (firstProduct?.production_location_id) {
            locationId = firstProduct.production_location_id;
          }
        }
      }
    }

    if (!locationId && item.product_id) {
      const product = products.find((p: any) => p.id === item.product_id);
      if (product?.production_location_id) {
        locationId = product.production_location_id;
      }
    }

    if (!locationId) continue;

    inserts.push({
      restaurant_id: restaurantId,
      order_id: orderId,
      order_item_id: item.id,
      production_location_id: locationId,
      status: 'new',
      name_snapshot: item.product_name,
      notes_snapshot: item.notes,
      quantity: item.quantity,
      opened_at: now,
      created_at: now,
      updated_at: now,
    });
  }

  if (inserts.length === 0) return;

  const { error } = await supabase
    .from("kds_ticket_items")
    .upsert(inserts, { onConflict: "order_item_id,production_location_id", ignoreDuplicates: true });

  if (error) {
    console.error("[KDS] Error creating ticket items:", error);
    return;
  }

  // Log events
  for (const insert of inserts) {
    await supabase.from("kds_events").insert({
      restaurant_id: restaurantId,
      order_id: orderId,
      order_item_id: insert.order_item_id,
      production_location_id: insert.production_location_id,
      event_type: "ticket_created",
      payload: { name: insert.name_snapshot, quantity: insert.quantity },
    } as any);
  }
}
